import cookie from 'js-cookie'
import constants from 'common/helpers/constants'

import WindowResolver, { logToConsole } from './WindowResolver'


type FBQPixel = 'standard' | 'special' | 'makeup'
type FBQTrackMethods = 'trackSingle' | 'trackSingleCustom' | 'track' | 'trackCustom'
type FBQArgsForCall = [ FBQTrackMethods, string, string ] | [ FBQTrackMethods, string ]

declare global {
  interface Window {
    fbq(...args: any[]): void
    __fbPixels: { [name in FBQPixel]: string }
  }
}
/*

  From docs:

  window.fbq('track', ...)

  Usage:

  fbq.track(...)
  fbq.trackWithPixel(eventName, pixelName, ...) to force usage of specified pixel name (from constants)

 */


// https://developers.facebook.com/docs/facebook-pixel/implementation/conversion-tracking/#standard-events
const standardEvents: string[] = [
  'AddPaymentInfo',
  'AddToCart',
  'AddToWishlist',
  'CompleteRegistration',
  'Contact',
  'CustomizeProduct',
  'Donate',
  'FindLocation',
  'InitiateCheckout',
  'Lead',
  'PageView',
  'Purchase',
  'Schedule',
  'Search',
  'StartTrial',
  'SubmitApplication',
  'Subscribe',
  'ViewContent',
]

const getPixelId = (pixelName: FBQPixel): null | string => {
  const pixels                  = window.__fbPixels
  const specialFlowNameCookie   = constants.cookieNames.specialFlowName
  const isFacebookSpecialFlow   = cookie.get(specialFlowNameCookie) === constants.specialFlowNames.facebook
  const isMakeupIntroFlow       = Boolean(cookie.get(constants.cookieNames.makeupIntroFlow))
  const isSkincareIntroFlow     = Boolean(cookie.get(constants.cookieNames.skincareIntroFlow))

  if (!pixels) {
    return null
  }

  if (pixelName) {
    return pixels[pixelName]
  }

  if (isMakeupIntroFlow || isSkincareIntroFlow) {
    return pixels[constants.facebookPixels.makeup]
  }

  if (isFacebookSpecialFlow) {
    return pixels[constants.facebookPixels.special]
  }

  return pixels[constants.facebookPixels.standard]
}

const getMethodName = (eventName: string): FBQTrackMethods => {
  const withMultiplePixels  = Boolean(window.__fbPixels)
  const isStandardEvent     = standardEvents.includes(eventName)

  if (withMultiplePixels) {
    return isStandardEvent ? 'trackSingle' : 'trackSingleCustom'
  }

  return isStandardEvent ? 'track' : 'trackCustom'
}

const getArgsForCall = (eventName: string, pixelName?: FBQPixel): FBQArgsForCall => {
  const pixel = getPixelId(pixelName)
  const methodName = getMethodName(eventName)

  if (pixel) {
    return [ methodName, pixel, eventName]
  }

  return [ methodName, eventName ]
}

const eventHandler = (
  methodName: FBQTrackMethods | 'trackWithPixel', eventName: string, ...args: any[]
): void => {
  let argsForCall: FBQArgsForCall[]

  if (methodName === 'trackWithPixel') {
    const [ pixelName, ...restArgs ] = args

    argsForCall = [ ...getArgsForCall(eventName, pixelName), ...restArgs ]
  }
  else {
    argsForCall = [ ...getArgsForCall(eventName), ...args ]
  }

  const isFreeTrialOrgFlow = Boolean(cookie.get(constants.cookieNames.isFreeTrialOrgFlow))

  if (!isFreeTrialOrgFlow) {
    window.fbq(...argsForCall)
  }
}

interface IFBQ {
  track(eventName: string, data?: { [key: string]: any }): void
  trackWithPixel(eventName: string, pixelName: FBQPixel, data?: { [key: string]: any }): void
}

const fbq = new WindowResolver({
  name: 'fbq',
  methods: {
    init: true,
    track: true,
    trackCustom: true, // TODO do we need this?
    trackWithPixel: true, // fbq.trackWithPixel({eventName}, {pixelName}, ...args)
  },
  resolver: () => Boolean(window.fbq),
  eventHandler,
  logToConsole: (
    name, methodName: FBQTrackMethods | 'trackWithPixel',
    eventName: string, pixelName: FBQPixel, ...args: any[]
  ) => {
    const isForcedPixel = methodName === 'trackWithPixel'

    // we take "pixelName" from arguments, but there can be no actual pixel name in args
    // so pixelName variable can contain some other argument value
    if (!isForcedPixel && pixelName) {
      args = [ pixelName, ...args ]
    }

    const [ realMethodName, ...callArgs ] = getArgsForCall(eventName, isForcedPixel ? pixelName : null)
    const restArgs = [ ...callArgs, ...args ]

    logToConsole(name, realMethodName, ...restArgs)
  },
}) as unknown as IFBQ


export default fbq

export {
  eventHandler,
}
